import { useState, useEffect, useCallback } from "react";
import {
  useAccount,
  useWriteContract,
  useWaitForTransactionReceipt,
} from "wagmi";
import { type Address, parseUnits, formatUnits, maxUint256 } from "viem";
import {
  Droplets,
  ZoomIn,
  BarChart2,
  Plus,
  Loader2,
  ExternalLink,
  Zap,
  Cog,
  AlertCircle,
} from "lucide-react";
import { TokenSelector } from "../components/swap/TokenSelector";
import { NATIVE_JASMY, WJASMY_TOKEN, type TokenInfo } from "../config/tokens";
import { ADDRESSES, V2_ROUTER_ABI, ERC20_ABI } from "../config/contracts";
import {
  CL_FEE_TIERS,
  BIN_STEPS,
  CL_POSITION_MANAGER_ABI,
  BIN_POSITION_MANAGER_ABI,
  sortCurrencies,
  clParameters,
  binParameters,
  NO_HOOKS,
} from "../config/v4Contracts";
import { useTokenBalance } from "../hooks/useTokenBalance";
import { useTokenAllowance } from "../hooks/useTokenAllowance";
import {
  cn,
  explorerUrl,
  formatTokenAmount,
  parseTokenAmount,
  getDeadline,
} from "../lib/utils";

// ─── Types ───────────────────────────────────────────────────────────────────

type PoolTab = "v2" | "cl" | "bin";
type HookOption = "none" | "autocompound" | "vortex" | "custom";

const POOL_TABS: Array<{
  id: PoolTab;
  label: string;
  icon: typeof Droplets;
  description: string;
}> = [
  {
    id: "v2",
    label: "V2 AMM",
    icon: Droplets,
    description:
      "Classic AMM -- full-range liquidity across all prices. Pool is auto-created when you add the first liquidity.",
  },
  {
    id: "cl",
    label: "Concentrated (CL)",
    icon: ZoomIn,
    description:
      "V4 Infinity concentrated liquidity. Set a price range for up to 4000x capital efficiency. Pool is created when the first position is minted.",
  },
  {
    id: "bin",
    label: "Bin Liquidity",
    icon: BarChart2,
    description:
      "V4 Infinity Liquidity Book. Extreme precision at fixed price bins, behaves like a limit-order book. Pool is created when the first position is added.",
  },
];

const HOOK_OPTIONS: Array<{
  id: HookOption;
  label: string;
  description: string;
  available: boolean;
}> = [
  {
    id: "none",
    label: "No Hook",
    description: "Standard pool without any hooks attached.",
    available: true,
  },
  {
    id: "autocompound",
    label: "AutoCompound",
    description:
      "Automatically reinvests earned fees back into your position. 0.404% x404 protocol fee on compounded yield.",
    available: true,
  },
  {
    id: "vortex",
    label: "VortexPool",
    description:
      "Dynamic fee adjustment based on volatility. Earns more during high-volume periods.",
    available: false,
  },
  {
    id: "custom",
    label: "Custom Hook",
    description: "Provide a custom hook contract address to attach to this pool.",
    available: true,
  },
];

// ─── Component ───────────────────────────────────────────────────────────────

export function PoolCreatorPage() {
  const { address: account, isConnected } = useAccount();

  // Tab state
  const [tab, setTab] = useState<PoolTab>("v2");
  const activeTab = POOL_TABS.find((t) => t.id === tab)!;

  // Token selection
  const [tokenA, setTokenA] = useState<TokenInfo>(NATIVE_JASMY);
  const [tokenB, setTokenB] = useState<TokenInfo>(WJASMY_TOKEN);

  // Amounts
  const [amountA, setAmountA] = useState("");
  const [amountB, setAmountB] = useState("");

  // CL fee tier
  const [selectedFeeTier, setSelectedFeeTier] = useState(0); // index into CL_FEE_TIERS

  // BIN step
  const [selectedBinStep, setSelectedBinStep] = useState(0); // index into BIN_STEPS

  // Hook selection
  const [hookOption, setHookOption] = useState<HookOption>("none");
  const [customHookAddress, setCustomHookAddress] = useState("");

  // Balances
  const { balance: balA, decimals: decA } = useTokenBalance(
    tokenA.isNative ? undefined : tokenA.address
  );
  const { balance: balB, decimals: decB } = useTokenBalance(
    tokenB.isNative ? undefined : tokenB.address
  );

  // Parsed amounts
  const parsedA = parseTokenAmount(amountA, tokenA.decimals);
  const parsedB = parseTokenAmount(amountB, tokenB.decimals);

  // Approvals for V2
  const tokenAForApproval = tokenA.isNative ? undefined : tokenA.address;
  const tokenBForApproval = tokenB.isNative ? undefined : tokenB.address;

  const {
    needsApproval: needsApprovalA,
    approve: approveA,
    isApproving: isApprovingA,
  } = useTokenAllowance(tokenAForApproval, ADDRESSES.V2_ROUTER, parsedA);

  const {
    needsApproval: needsApprovalB,
    approve: approveB,
    isApproving: isApprovingB,
  } = useTokenAllowance(tokenBForApproval, ADDRESSES.V2_ROUTER, parsedB);

  // V2 Create Pool via addLiquidity (auto-creates pair if needed)
  const {
    writeContract: writeV2,
    isPending: isV2Pending,
    data: v2TxHash,
  } = useWriteContract();
  const { isLoading: isV2Waiting, isSuccess: v2Success } =
    useWaitForTransactionReceipt({ hash: v2TxHash });

  // Resolve actual addresses for contract calls
  const resolveAddress = (t: TokenInfo): Address =>
    t.isNative ? ADDRESSES.WJASMY : t.address;

  const handleCreateV2 = useCallback(() => {
    if (!account) return;
    const deadline = getDeadline(20);

    // If one token is native JASMY, use addLiquidityJASMY
    if (tokenA.isNative || tokenB.isNative) {
      const otherToken = tokenA.isNative ? tokenB : tokenA;
      const otherAmount = tokenA.isNative ? parsedB : parsedA;
      const jasmyAmount = tokenA.isNative ? parsedA : parsedB;

      writeV2({
        address: ADDRESSES.V2_ROUTER,
        abi: V2_ROUTER_ABI,
        functionName: "addLiquidityJASMY",
        args: [
          otherToken.address,
          otherAmount,
          (otherAmount * 95n) / 100n, // 5% slippage
          (jasmyAmount * 95n) / 100n,
          account,
          deadline,
        ],
        value: jasmyAmount,
      });
    } else {
      // Both are ERC20 tokens
      writeV2({
        address: ADDRESSES.V2_ROUTER,
        abi: V2_ROUTER_ABI,
        functionName: "addLiquidity",
        args: [
          tokenA.address,
          tokenB.address,
          parsedA,
          parsedB,
          (parsedA * 95n) / 100n,
          (parsedB * 95n) / 100n,
          account,
          deadline,
        ],
      });
    }
  }, [account, tokenA, tokenB, parsedA, parsedB, writeV2]);

  // Validation
  const isTokensSame =
    tokenA.address === tokenB.address && tokenA.isNative === tokenB.isNative;
  const hasAmounts = parsedA > 0n && parsedB > 0n;
  const insufficientA = parsedA > balA;
  const insufficientB = parsedB > balB;
  const canCreate = isConnected && !isTokensSame && hasAmounts && !insufficientA && !insufficientB;

  // Hook address resolution
  const resolvedHookAddress: Address =
    hookOption === "custom" && customHookAddress.length === 42
      ? (customHookAddress as Address)
      : NO_HOOKS;

  return (
    <div className="relative min-h-[calc(100vh-4rem)]">
      {/* Background orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-60 -right-60 w-[800px] h-[800px] rounded-full bg-[#39E6FF]/[0.03] blur-[150px]" />
        <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full bg-primary/[0.04] blur-[120px]" />
      </div>

      <div className="relative flex flex-col items-center justify-start pt-10 sm:pt-14 pb-20 px-4">
        {/* Header */}
        <div className="inline-flex items-center gap-2 bg-[#39E6FF]/10 border border-[#39E6FF]/20 rounded-full px-4 py-1.5 text-sm text-[#39E6FF] font-medium mb-4">
          <Plus className="w-3.5 h-3.5" />
          Pool Creator
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold gradient-text mb-2 text-center">
          Create Pool
        </h1>
        <p className="text-text-secondary text-sm max-w-lg mx-auto text-center mb-8">
          Launch a new liquidity pool on JasmyChain across V2, Concentrated, or Bin pool types
        </p>

        {/* Main card */}
        <div className="w-full max-w-[540px]">
          {/* Pool type tabs */}
          <div className="mb-6">
            <div className="flex gap-1 p-1 bg-surface/50 rounded-card">
              {POOL_TABS.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setTab(id)}
                  className={cn(
                    "flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-button text-sm font-medium transition-all",
                    tab === id
                      ? "bg-primary/15 text-primary"
                      : "text-text-secondary hover:text-text-primary"
                  )}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              ))}
            </div>
            <div className="mt-3 px-1">
              <p className="text-text-tertiary text-xs leading-relaxed">
                {activeTab.description}
              </p>
            </div>
          </div>

          {/* Token pair selection */}
          <div className="glass-card p-5 mb-4">
            <h3 className="text-sm font-semibold text-text-primary mb-4 flex items-center gap-2">
              <Droplets className="w-4 h-4 text-[#39E6FF]" />
              Token Pair
            </h3>

            <div className="grid grid-cols-2 gap-4">
              {/* Token A */}
              <div>
                <label className="text-xs text-text-tertiary mb-1.5 block">
                  Token A
                </label>
                <TokenSelector
                  selectedToken={tokenA}
                  onSelect={setTokenA}
                  otherToken={tokenB}
                />
                <div className="mt-1.5 text-[10px] text-text-tertiary">
                  Balance: {formatTokenAmount(balA, decA)}
                </div>
              </div>

              {/* Token B */}
              <div>
                <label className="text-xs text-text-tertiary mb-1.5 block">
                  Token B
                </label>
                <TokenSelector
                  selectedToken={tokenB}
                  onSelect={setTokenB}
                  otherToken={tokenA}
                />
                <div className="mt-1.5 text-[10px] text-text-tertiary">
                  Balance: {formatTokenAmount(balB, decB)}
                </div>
              </div>
            </div>

            {isTokensSame && (
              <div className="mt-3 flex items-center gap-2 text-error text-xs">
                <AlertCircle className="w-3.5 h-3.5" />
                Select two different tokens
              </div>
            )}
          </div>

          {/* Pool parameters (CL / BIN) */}
          {tab === "cl" && (
            <div className="glass-card p-5 mb-4">
              <h3 className="text-sm font-semibold text-text-primary mb-4 flex items-center gap-2">
                <Cog className="w-4 h-4 text-primary" />
                Fee Tier
              </h3>
              <div className="grid grid-cols-4 gap-2">
                {CL_FEE_TIERS.map((tier, i) => (
                  <button
                    key={tier.fee}
                    onClick={() => setSelectedFeeTier(i)}
                    className={cn(
                      "p-3 rounded-button text-center transition-all border",
                      selectedFeeTier === i
                        ? "bg-primary/15 border-primary/40 text-primary"
                        : "bg-surface/30 border-border text-text-secondary hover:border-border-bright"
                    )}
                  >
                    <div className="text-sm font-bold">{tier.label}</div>
                    <div className="text-[10px] text-text-tertiary mt-0.5">
                      tick {tier.tickSpacing}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {tab === "bin" && (
            <div className="glass-card p-5 mb-4">
              <h3 className="text-sm font-semibold text-text-primary mb-4 flex items-center gap-2">
                <Cog className="w-4 h-4 text-primary" />
                Bin Step
              </h3>
              <div className="grid grid-cols-4 gap-2">
                {BIN_STEPS.map((step, i) => (
                  <button
                    key={step.binStep}
                    onClick={() => setSelectedBinStep(i)}
                    className={cn(
                      "p-3 rounded-button text-center transition-all border",
                      selectedBinStep === i
                        ? "bg-primary/15 border-primary/40 text-primary"
                        : "bg-surface/30 border-border text-text-secondary hover:border-border-bright"
                    )}
                  >
                    <div className="text-sm font-bold">{step.label}</div>
                    <div className="text-[10px] text-text-tertiary mt-0.5">
                      step {step.binStep}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Hook selection (CL / BIN only) */}
          {(tab === "cl" || tab === "bin") && (
            <div className="glass-card p-5 mb-4">
              <h3 className="text-sm font-semibold text-text-primary mb-4 flex items-center gap-2">
                <Zap className="w-4 h-4 text-[#39E6FF]" />
                Hook
              </h3>
              <div className="space-y-2">
                {HOOK_OPTIONS.map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => opt.available && setHookOption(opt.id)}
                    disabled={!opt.available}
                    className={cn(
                      "w-full flex items-start gap-3 p-3 rounded-button text-left transition-all border",
                      hookOption === opt.id
                        ? "bg-primary/10 border-primary/30"
                        : opt.available
                          ? "bg-surface/20 border-border hover:border-border-bright"
                          : "bg-surface/10 border-border opacity-50 cursor-not-allowed"
                    )}
                  >
                    {/* Radio indicator */}
                    <div
                      className={cn(
                        "w-4 h-4 rounded-full border-2 mt-0.5 shrink-0 flex items-center justify-center",
                        hookOption === opt.id
                          ? "border-primary"
                          : "border-text-tertiary"
                      )}
                    >
                      {hookOption === opt.id && (
                        <div className="w-2 h-2 rounded-full bg-primary" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-text-primary">
                          {opt.label}
                        </span>
                        {!opt.available && (
                          <span className="text-[10px] font-semibold bg-primary/10 text-primary border border-primary/20 rounded-full px-2 py-0.5">
                            Coming Soon
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-text-tertiary mt-0.5 leading-relaxed">
                        {opt.description}
                      </p>
                    </div>
                  </button>
                ))}
              </div>

              {/* Custom hook address input */}
              {hookOption === "custom" && (
                <div className="mt-3">
                  <input
                    type="text"
                    value={customHookAddress}
                    onChange={(e) => setCustomHookAddress(e.target.value)}
                    placeholder="0x... hook contract address"
                    className="input-field font-mono text-sm"
                  />
                  {customHookAddress &&
                    !/^0x[a-fA-F0-9]{40}$/.test(customHookAddress) && (
                      <p className="text-error text-xs mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        Invalid address format
                      </p>
                    )}
                </div>
              )}
            </div>
          )}

          {/* Initial liquidity */}
          <div className="glass-card p-5 mb-4">
            <h3 className="text-sm font-semibold text-text-primary mb-4 flex items-center gap-2">
              <Plus className="w-4 h-4 text-green-400" />
              Initial Liquidity
            </h3>

            {/* Amount A */}
            <div className="token-input-row mb-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-text-tertiary">{tokenA.symbol}</span>
                <button
                  onClick={() =>
                    setAmountA(formatUnits(balA, tokenA.decimals))
                  }
                  className="text-[10px] text-primary hover:underline"
                >
                  MAX: {formatTokenAmount(balA, tokenA.decimals)}
                </button>
              </div>
              <input
                type="number"
                min="0"
                step="any"
                value={amountA}
                onChange={(e) => setAmountA(e.target.value)}
                placeholder="0.0"
                className="amount-input"
              />
              {insufficientA && (
                <p className="text-error text-[10px] mt-1">Insufficient balance</p>
              )}
            </div>

            {/* Amount B */}
            <div className="token-input-row">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-text-tertiary">{tokenB.symbol}</span>
                <button
                  onClick={() =>
                    setAmountB(formatUnits(balB, tokenB.decimals))
                  }
                  className="text-[10px] text-primary hover:underline"
                >
                  MAX: {formatTokenAmount(balB, tokenB.decimals)}
                </button>
              </div>
              <input
                type="number"
                min="0"
                step="any"
                value={amountB}
                onChange={(e) => setAmountB(e.target.value)}
                placeholder="0.0"
                className="amount-input"
              />
              {insufficientB && (
                <p className="text-error text-[10px] mt-1">Insufficient balance</p>
              )}
            </div>
          </div>

          {/* Pool summary (CL/BIN preview) */}
          {(tab === "cl" || tab === "bin") && (
            <div className="glass-card p-5 mb-4">
              <h3 className="text-sm font-semibold text-text-primary mb-3">
                Pool Preview
              </h3>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-text-tertiary">Pool Type</span>
                  <span className="text-text-primary font-medium">
                    V4 Infinity {tab === "cl" ? "Concentrated" : "Bin"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-tertiary">Pair</span>
                  <span className="text-text-primary font-medium">
                    {tokenA.symbol} / {tokenB.symbol}
                  </span>
                </div>
                {tab === "cl" && (
                  <div className="flex justify-between">
                    <span className="text-text-tertiary">Fee Tier</span>
                    <span className="text-text-primary font-medium">
                      {CL_FEE_TIERS[selectedFeeTier].label} (tick spacing{" "}
                      {CL_FEE_TIERS[selectedFeeTier].tickSpacing})
                    </span>
                  </div>
                )}
                {tab === "bin" && (
                  <div className="flex justify-between">
                    <span className="text-text-tertiary">Bin Step</span>
                    <span className="text-text-primary font-medium">
                      {BIN_STEPS[selectedBinStep].label} (step{" "}
                      {BIN_STEPS[selectedBinStep].binStep})
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-text-tertiary">Hook</span>
                  <span className="text-text-primary font-medium">
                    {hookOption === "none"
                      ? "None"
                      : hookOption === "autocompound"
                        ? "AutoCompound"
                        : hookOption === "vortex"
                          ? "VortexPool"
                          : customHookAddress
                            ? `${customHookAddress.slice(0, 8)}...`
                            : "Custom"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-tertiary">Pool Manager</span>
                  <span className="text-text-primary font-mono text-[10px]">
                    {tab === "cl"
                      ? `${ADDRESSES.CL_POOL_MANAGER.slice(0, 8)}...${ADDRESSES.CL_POOL_MANAGER.slice(-4)}`
                      : `${ADDRESSES.BIN_POOL_MANAGER.slice(0, 8)}...${ADDRESSES.BIN_POOL_MANAGER.slice(-4)}`}
                  </span>
                </div>
              </div>
              <div className="mt-3 p-2 rounded-lg bg-primary/5 border border-primary/10">
                <p className="text-[10px] text-text-tertiary leading-relaxed">
                  The pool will be created when the first liquidity position is
                  minted via the V4 Position Manager. Use the Liquidity page to
                  add your position after creating the pool parameters here.
                </p>
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="space-y-2">
            {/* Approval buttons for V2 */}
            {tab === "v2" && needsApprovalA && !tokenA.isNative && (
              <button
                className="btn-secondary w-full flex items-center justify-center gap-2"
                onClick={approveA}
                disabled={isApprovingA}
              >
                {isApprovingA ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Approving {tokenA.symbol}...
                  </>
                ) : (
                  `Approve ${tokenA.symbol}`
                )}
              </button>
            )}

            {tab === "v2" && needsApprovalB && !tokenB.isNative && (
              <button
                className="btn-secondary w-full flex items-center justify-center gap-2"
                onClick={approveB}
                disabled={isApprovingB}
              >
                {isApprovingB ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Approving {tokenB.symbol}...
                  </>
                ) : (
                  `Approve ${tokenB.symbol}`
                )}
              </button>
            )}

            {/* Main action */}
            {!isConnected ? (
              <div className="btn-primary w-full text-center opacity-60 cursor-not-allowed">
                Connect Wallet
              </div>
            ) : tab === "v2" ? (
              <button
                className="btn-primary w-full flex items-center justify-center gap-2"
                onClick={handleCreateV2}
                disabled={
                  !canCreate ||
                  isV2Pending ||
                  isV2Waiting ||
                  needsApprovalA ||
                  needsApprovalB
                }
              >
                {isV2Pending || isV2Waiting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating Pool...
                  </>
                ) : v2Success ? (
                  "Pool Created"
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Create V2 Pool + Add Liquidity
                  </>
                )}
              </button>
            ) : (
              <div className="space-y-2">
                <div className="btn-primary w-full text-center opacity-60 cursor-default flex items-center justify-center gap-2">
                  <Zap className="w-4 h-4" />
                  {tab === "cl"
                    ? "Create CL Pool via Liquidity Page"
                    : "Create Bin Pool via Liquidity Page"}
                </div>
                <p className="text-text-tertiary text-[10px] text-center">
                  V4 pools are created when the first position is minted. Navigate to
                  the{" "}
                  <a
                    href="/liquidity"
                    className="text-primary hover:underline"
                  >
                    Liquidity page
                  </a>{" "}
                  and select the {tab === "cl" ? "Concentrated" : "Bin"} tab to
                  add your position with the parameters configured above.
                </p>
              </div>
            )}
          </div>

          {/* Success state */}
          {v2Success && v2TxHash && (
            <div className="mt-4 glass-card p-4 text-center">
              <p className="text-green-400 text-sm font-medium mb-2">
                Pool created successfully
              </p>
              <a
                href={explorerUrl("tx", v2TxHash)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
              >
                <ExternalLink className="w-3 h-3" />
                View Transaction
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
